/**
 * ProfileConflictModal — shown when user logs in and both local + server profiles exist.
 * Three options: Use Cloud, Use Local, Merge Both.
 */

import { Cloud, Monitor, GitMerge } from 'lucide-react';
import type { Profile } from '@/store/profile.store';
import './profile-conflict-modal.css';

interface ProfileSummary {
  name: string;
  email: string;
  experienceCount: number;
  skillsCount: number;
  updatedAt?: string;
}

function summarize(p: Profile): ProfileSummary {
  return {
    name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'No name',
    email: p.email || 'No email',
    experienceCount: p.experience?.length || 0,
    skillsCount: p.skills?.length || 0,
    updatedAt: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : undefined,
  };
}

function ProfileCard({
  title,
  icon,
  summary,
}: {
  title: string;
  icon: React.ReactNode;
  summary: ProfileSummary;
}) {
  return (
    <div className="conflict-card">
      <div className="conflict-card__header">
        {icon}
        <span>{title}</span>
      </div>
      <p className="conflict-card__name">{summary.name}</p>
      <p className="conflict-card__email">{summary.email}</p>
      <div className="conflict-card__stats">
        <span>{summary.experienceCount} positions</span>
        <span>{summary.skillsCount} skills</span>
      </div>
      {summary.updatedAt && (
        <p className="conflict-card__date">Updated: {summary.updatedAt}</p>
      )}
    </div>
  );
}

interface ProfileConflictModalProps {
  localProfile: Profile;
  serverProfile: Profile;
  onUseCloud: () => void;
  onUseLocal: () => void;
  onMerge: () => void;
  isLoading?: boolean;
}

export function ProfileConflictModal({
  localProfile,
  serverProfile,
  onUseCloud,
  onUseLocal,
  onMerge,
  isLoading = false,
}: ProfileConflictModalProps) {
  const local = summarize(localProfile);
  const server = summarize(serverProfile);

  return (
    <div className="conflict-overlay">
      <div className="conflict-modal">
        <h2 className="conflict-modal__title">Profile Conflict</h2>
        <p className="conflict-modal__subtitle">
          You have profiles in two places. Choose how to proceed:
        </p>

        <div className="conflict-modal__cards">
          <ProfileCard title="Cloud Profile" icon={<Cloud size={16} />} summary={server} />
          <ProfileCard title="Local Profile" icon={<Monitor size={16} />} summary={local} />
        </div>

        <div className="conflict-modal__actions">
          <button
            className="conflict-btn conflict-btn--primary"
            onClick={onUseCloud}
            disabled={isLoading}
          >
            <Cloud size={16} /> Use Cloud Profile
          </button>
          <p className="conflict-btn__hint">Replaces your local profile with the cloud version</p>

          <button
            className="conflict-btn conflict-btn--outline"
            onClick={onUseLocal}
            disabled={isLoading}
          >
            <Monitor size={16} /> Use Local Profile
          </button>
          <p className="conflict-btn__hint">Keeps your local profile and updates the cloud</p>

          <button
            className="conflict-btn conflict-btn--outline"
            onClick={onMerge}
            disabled={isLoading}
          >
            <GitMerge size={16} /> Merge Both
          </button>
          <p className="conflict-btn__hint">Combines skills, experience, and education from both</p>
        </div>
      </div>
    </div>
  );
}
